import { useMemo } from 'react';
import { Waves } from 'lucide-react';
import { views } from '@/copy';
import { Button } from '@/ui/design-system/components';
import MultiPredictionTimeSeriesChart, {
    getSeriesColors,
} from 'components/DataVis/Charts/MultiPredictionTimeSeriesChart';
import { shapeTimeSeries } from 'utils/hooks/sourceDataShaping';
import AssistantVisualFrame from '../primitives/AssistantVisualFrame';
import InlineTableHeader from '../primitives/InlineTableHeader';
import MetricTile from '../primitives/MetricTile';
import {
    CHART_HEIGHT,
    PlainChartWrapper,
    STROKE_WIDTH,
    buildListChrome,
    type EntityListSeries,
    type ListChartSpec,
} from './entityListModel';
import type { EntityTimeSeriesRow, VisualProps } from '../types';

function buildEntityListCombinedModel({
    input,
    output,
    spec,
    customTitle,
}: VisualProps & { spec: ListChartSpec; customTitle?: string }) {
    const chrome = buildListChrome({ input, output, spec, customTitle });

    const shaped = shapeTimeSeries(
        (output?._combined_time_series ?? []) as EntityTimeSeriesRow[],
    );
    const series: EntityListSeries[] = shaped
        ? [
              {
                  id: 'combined',
                  name: 'Combined',
                  color: getSeriesColors(0),
                  timeSeries: shaped as unknown[],
              },
          ]
        : [];

    return {
        ...chrome,
        series,
        tiles: spec.tiles(output?._aggregate),
    };
}

const EntityListCombinedView = ({
    input,
    output,
    spec,
    Frame = AssistantVisualFrame,
    title,
    onToggleAggregate,
}: VisualProps & { spec: ListChartSpec }) => {
    const model = useMemo(
        () =>
            buildEntityListCombinedModel({
                input,
                output,
                spec,
                customTitle: title,
            }),
        [input, output, spec, title],
    );

    const hasSeries = model.series.length > 0;

    return (
        <Frame breadcrumb={model.breadcrumb}>
            <div className="bg-card border border-border rounded-xl shadow-xs overflow-hidden">
                <InlineTableHeader title={model.title} link={model.link} />
                {model.rows.length === 0 ? (
                    <div className="px-3.5 py-4 text-xs text-muted-foreground text-center">
                        {views.assistantVisuals.noRows}
                    </div>
                ) : (
                    <div className="flex flex-col gap-3 p-4">
                        {onToggleAggregate && (
                            <div className="flex">
                                <Button
                                    variant="outline"
                                    size="sm"
                                    onClick={() => onToggleAggregate(false)}
                                >
                                    <Waves />
                                    View separated
                                </Button>
                            </div>
                        )}
                        {hasSeries ? (
                            <MultiPredictionTimeSeriesChart
                                chartHeight={CHART_HEIGHT}
                                data={model.series}
                                Tooltip={spec.Tooltip}
                                Wrapper={PlainChartWrapper}
                                showAxes={false}
                                strokeWidth={STROKE_WIDTH}
                            />
                        ) : (
                            <div
                                className="flex items-center justify-center rounded-md bg-muted text-xs text-muted-foreground"
                                style={{ height: CHART_HEIGHT }}
                            >
                                {views.assistantVisuals.noTrendLines}
                            </div>
                        )}
                        <div className="grid grid-cols-3 gap-2">
                            {model.tiles.map((tile) => (
                                <MetricTile
                                    compact
                                    key={tile.label}
                                    {...tile}
                                />
                            ))}
                        </div>
                    </div>
                )}
            </div>
        </Frame>
    );
};

export default EntityListCombinedView;
